import { useState } from 'react';
import { motion } from 'framer-motion';
import { MarketingNav, MarketingFooter } from '../../components/marketing/MarketingLayout';
import { Mail, Phone, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';

const DUBAI_BG = 'https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=1920&q=40';

function Reveal({ children, delay = 0, className = '' }) {
  return <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay }} className={className}>{children}</motion.div>;
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div className="bg-[#FCFCFC] text-[#0A0A0A] min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <MarketingNav />

      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${DUBAI_BG})`, opacity: 0.05 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Get In Touch</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Let's Talk Business
            </h1>
            <p className="text-base lg:text-lg text-[#52525B] max-w-xl mx-auto">
              Ready to simplify your operations? Our team in Dubai is here to help.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <Reveal className="lg:col-span-2">
              <div className="space-y-8">
                {[
                  { icon: Mail, label: 'Email', value: 'info@sgkapps.com', sub: 'We respond within 24 hours' },
                  { icon: Phone, label: 'Phone', value: '+971 XX XXX XXXX', sub: 'Sun - Thu, 9:00 - 18:00 GST' },
                  { icon: MapPin, label: 'Office', value: 'Dubai, UAE', sub: 'SGK Apps Headquarters' },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#0f172a]/8 flex items-center justify-center shrink-0">
                      <c.icon size={20} className="text-[#0f172a]" />
                    </div>
                    <div>
                      <div className="text-sm font-bold">{c.label}</div>
                      <div className="text-sm text-[#0f172a]">{c.value}</div>
                      <div className="text-xs text-[#71717A] mt-0.5">{c.sub}</div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-zinc-200">
                  <p className="text-xs font-semibold text-[#52525B] mb-2">Suitable for:</p>
                  <div className="flex flex-wrap gap-2">
                    {['Accountants', 'Auditors', 'SMEs', 'Multi-Company Firms', 'Tax Consultants'].map(k => (
                      <span key={k} className="text-xs px-3 py-1.5 rounded-full bg-[#0f172a]/5 text-[#0f172a]/70 font-medium">{k}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Contact Form */}
            <Reveal delay={0.15} className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-semibold mb-1.5">Full Name</label>
                    <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg bg-zinc-50 border border-zinc-200 focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a] outline-none transition text-sm" placeholder="John Doe" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1.5">Email</label>
                    <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg bg-zinc-50 border border-zinc-200 focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a] outline-none transition text-sm" placeholder="john@company.com" data-testid="contact-email" />
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-sm font-semibold mb-1.5">Company Name</label>
                  <input type="text" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-zinc-50 border border-zinc-200 focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a] outline-none transition text-sm" placeholder="Company LLC" data-testid="contact-company" />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-1.5">Message</label>
                  <textarea required rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-zinc-50 border border-zinc-200 focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a] outline-none transition text-sm resize-none" placeholder="Tell us about your requirements..." data-testid="contact-message" />
                </div>
                <button type="submit" className="w-full bg-[#0f172a] text-white hover:bg-[#1e293b] rounded-lg font-semibold px-6 py-3.5 text-sm transition-colors flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                  {sent ? <><CheckCircle2 size={16} /> Message Sent!</> : <>Send Message <ArrowRight size={16} /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
